import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../services/authApi';
import { onAuthFailure, setAccessToken } from '../services/apiClient';
import { connectRealtime, disconnectRealtime } from '../services/realtimeClient';

const AuthContext = createContext();
const REFRESH_SESSION_KEY = 'servicedesk.hasRefreshSession';
let hydrationPromise = null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  const normalizeUser = (backendUser) => {
    const primaryRole = backendUser?.roles?.[0] || 'USER';
    const roleMap = {
      admin: 'ADMIN',
      developer: 'DEVELOPER',
      user: 'USER',
      super_admin: 'ADMIN',
      support_admin: 'ADMIN',
      end_user: 'USER'
    };
    return {
      id: backendUser?.id,
      name: backendUser?.name || `${backendUser?.firstName || ''} ${backendUser?.lastName || ''}`.trim() || backendUser?.email || 'User',
      email: backendUser?.email,
      role: roleMap[primaryRole] || primaryRole.toUpperCase(),
      roles: backendUser?.roles || [],
      organizationId: backendUser?.organizationId,
      publicId: backendUser?.publicId,
      designation: backendUser?.designation,
      permissions: backendUser?.permissions || [],
      team: backendUser?.team || 'ServiceDesk',
      avatar: backendUser?.avatar || '',
      kycVerified: backendUser?.kycVerified || false,
      hasServiceBlock: backendUser?.hasServiceBlock || false
    };
  };

  const setToken = (nextToken) => {
    setTokenState(nextToken);
    setAccessToken(nextToken);
  };

  useEffect(() => {
    onAuthFailure(() => {
      localStorage.removeItem(REFRESH_SESSION_KEY);
      setUser(null);
      setToken(null);
    });

    let mounted = true;
    async function hydrate() {
      const shouldAttemptRefresh = localStorage.getItem(REFRESH_SESSION_KEY) === 'true';
      if (!shouldAttemptRefresh) {
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }

      try {
        hydrationPromise ||= authApi.refresh();
        const refreshed = await hydrationPromise;
        if (!mounted) return;
        setToken(refreshed.accessToken);
        const currentUser = await authApi.me();
        if (mounted) setUser(normalizeUser(currentUser));
      } catch (error) {
        localStorage.removeItem(REFRESH_SESSION_KEY);
        if (mounted) {
          setUser(null);
          setToken(null);
        }
      } finally {
        hydrationPromise = null;
        if (mounted) setLoading(false);
      }
    }

    hydrate();
    return () => {
      mounted = false;
    };
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authApi.me();
      setUser(normalizeUser(currentUser));
    } catch (err) {
      console.error('Failed to refresh user profile:', err);
    }
  }, []);

  const login = useCallback(async (identityOrEmail, password) => {
    setAuthError('');
    const result = await authApi.login({ identity: identityOrEmail, email: identityOrEmail, password });
    localStorage.setItem(REFRESH_SESSION_KEY, 'true');
    setToken(result.accessToken);
    const currentUser = await authApi.me();
    setUser(normalizeUser(currentUser));
    return true;
  }, []);

  const requestOtp = useCallback(async (identityOrEmail) => {
    setAuthError('');
    await authApi.requestOtp({ identity: identityOrEmail, email: identityOrEmail });
    return true;
  }, []);

  const verifyOtp = useCallback(async (identityOrEmail, code) => {
    setAuthError('');
    const result = await authApi.verifyOtp({ identity: identityOrEmail, email: identityOrEmail, code });
    localStorage.setItem(REFRESH_SESSION_KEY, 'true');
    setToken(result.accessToken);
    const currentUser = await authApi.me();
    setUser(normalizeUser(currentUser));
    return true;
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem(REFRESH_SESSION_KEY);
    setUser(null);
    setToken(null);
    try {
      await authApi.logout();
    } catch {
      // Local logout state has already been cleared; remote logout failure is non-blocking.
    }
  }, []);

  const isAuthenticated = !!token;

  useEffect(() => {
    if (isAuthenticated) {
      connectRealtime({ enabled: import.meta.env.VITE_SSE_ENABLED !== 'false', token });
    } else {
      disconnectRealtime();
    }
    return () => disconnectRealtime();
  }, [isAuthenticated, token]);

  const value = useMemo(
    () => ({ user, setUser, token, login, requestOtp, verifyOtp, logout, isAuthenticated, loading, authError, setAuthError, refreshUser }),
    [user, token, login, requestOtp, verifyOtp, logout, isAuthenticated, loading, authError, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
